import React, { useEffect } from "react";
import { MenuInfo } from "rc-menu/es/interface";
import { Modal, message } from "antd";
import { useRequest } from "ahooks";
import { assign, cloneDeep, remove } from "lodash";
import { toJS } from "mobx";
import OrderDTO from "@/dto/order/OrderDTO";
import ShopDTO from "@/dto/shop/ShopDTO";
import OrderTab from "@/scenes/OrderManagement/enum/OrderTab";
import { useCompanySettingStore, useGlobalStore } from "@/store/hooks";
import { useOrderManagementStore } from "@/scenes/OrderManagement/store/OrderManagementStore";
import { useModalStore } from "@/hooks/Modal/hooks";
import useDeleteWaybillNoClick from "./useDeleteWaybillNoClick";
import usePrintOrderItemLabelClick from "./usePrintOrderItemLabelClick";
import useDownloadCustomization from "./useDownloadCustomization";
import useBatchCancelMergeOrderDialog from "../../../dialogs/BatchCancelMergeOrderDialog/useBatchCancelMergeOrderDialog";
import useSupplierBatchSyncOrders from "@/scenes/OrderManagement/hooks/useSupplierBatchSyncOrders";
import useCancelDropshippingOrdersClick from "./useCancelDropshippingOrdersClick";
import { pddFdsEwaybillCancel } from "@/services/api/ewaybill";
import { getIsSupplierManualShop, shouldShowDropShippingDetail } from "@/utils/ShopUtils";
import { lockOrder, unlockOrder } from "@/services/api/order";
import { confirm } from "@/utils";
import {
    getValidOrders,
    preValidateAndFilterOrdersOrExecOperation,
} from "../../../dialogs/DropShippingBatchPushOrderModal/services";
import { handleBatchSplitOrder } from "../../../dialogs/BatchSplitOrderDialog/BatchSplitOrderModal";
import batchCancelSplitOrders from "../services/batchCancelSplitOrders";
import { PushOrderResult } from "@/services/api/dropShipping/order";
import BatchCancelEwaybillNoModal from "../../../dialogs/BatchCancelEwaybillNoDialog/BatchCancelEwaybillNoModal";
import BatchDeleteEWaybillNoModal from "../../../dialogs/BatchDeleteEWaybillNoDialog/BatchDeleteEWaybillNoModal";
import BatchSetOrderMarkModal from "../../../dialogs/BatchSetOrderMarkDialog/BatchSetOrderMarkModal";
import HoldOrderModal from "../../../dialogs/HoldOrderDialog/HoldOrderModal";
import BatchReprintEwaybillModal from "../../../dialogs/BatchReprintEwaybillDialog/BatchReprintEwaybillModal";
import DropShippingBatchPushOrderModal from "../../../dialogs/DropShippingBatchPushOrderModal";
import BatchRefundDropShippingOrderModal from "../../../dialogs/BatchRefundDropShippingOrderModal";
import BatchUpdateIMEIModal from "../components/BatchUpdateIMEIModal";
import UpdateLogisticsCompanyDialog from "../../../dialogs/UpdateLogisticsCompanyDialog";
import UpdatePrintingStatusDialog from "../../../dialogs/UpdatePrintingStatusDialog";
import ExportOrderInfoModal from "../../../dialogs/ExportOrderInfoModal";
import ExportOrderExpressModal from "../../../dialogs/ExportOrderExpressModal";
import ExportOrderPickListModal from "../../../dialogs/ExportOrderPickListModal";
import ExportOrderPurchasingLabelModal from "../../../dialogs/ExportOrderPurchasingLabelModal";
import ExportShippingTemplateDialog from "../../../dialogs/ExportShippingTemplateDialog";
import UpdateShippingAddressModal from "../../../OrderTable/OrderTableHead/components/UpdateShippingAddressModal";
import ConfirmMergeOrderModal from "../../../dialogs/MergeMergeableOrderDialog/ConfirmMergeOrderModal";
import PrintQuantityInspectionCodeModal from "../../../dialogs/PrintQuantityInspectionCodeModal";
import openBatchUpdateSellerRemarksModal from "@/scenes/OrderManagementForAllShop/components/BottomOperationBar/OtherOperationsButton/components/services/batchUpdateSellerRemarks";
import { dzAdminHost, hasLoginLaihuoV2, pushOrderToFactory } from "@/services/lhSDK";
import { collectEvent } from "@/services/dataTrack";
import DataTrackEvent from "@/services/dataTrack/enum/DataTrackEvent";
import ShopTypeNameMap from "@/map/ShopTypeNameMap";
import OrderTag from "@/enum/OrderTag";
import OrderMergeSplitStatus from "@/enum/OrderMergeSplitStatus";
import OrderTabNameMap from "@/scenes/OrderManagement/map/OrderTabNameMap";
import { useCopyOrderInfoClick } from "./useCopyOrderInfoClick";
import Authority from "@/enum/Authority";
import usePrintPackingListClick from "../../hooks/usePrintPackingListClick";
import AppVersionNameMap from "@/map/AppVersionNameMap";
import { isEmpty } from "@/utils";
import { usePreShip } from "../../PreShipButton/usePreShip";
import { OperationKey } from "../services/otherOperationDefinitions";
import { execReissueOrder } from "../../ReissueButton/service";
import { execReshipOrder } from "../../ShipButton/services";
import useReship from "../../../dialogs/ReshipOrderDialog/useReship";
import SyncOrderProcessDialog from "../../../dialogs/SyncOrderProcessDialog";
import { useHistory } from "react-router";
import InstantShipStartModal from "@/scenes/OrderManagement/components/InstantShip/StartModal";
import InstantShipTaskModal from "@/scenes/OrderManagement/components/InstantShip/TaskModal";

export interface UseOtherOperationActionsProps {
    tab: OrderTab;
    shop: ShopDTO;
    getSelectedOrders(): OrderDTO[];
    onMenuClick?(info: MenuInfo): void;
    onLoadingChange?(loading: boolean): void;
}

export const useOtherOperationActions = (props: UseOtherOperationActionsProps) => {
    const { tab, shop, getSelectedOrders, onMenuClick, onLoadingChange } = props;

    const modalStore = useModalStore();
    const orderManagementStore = useOrderManagementStore();
    const globalStore = useGlobalStore();
    const companySettingStore = useCompanySettingStore();

    const fulfillmentType = orderManagementStore.table?.listOptions?.fulfillmentType;

    const { runAsync: handleDeleteWaybillNoClick, loading: deleteWaybillNoLoading } =
        useDeleteWaybillNoClick(getSelectedOrders);

    const { handlePrintOrderItemLabel } = usePrintOrderItemLabelClick(getSelectedOrders);

    const onRefresh = () => {
        orderManagementStore.fetchOrders(shop?.id, tab);
    };

    const { handlePrint: handlePrintPackingList } = usePrintPackingListClick({
        getSelectedOrders,
    });

    const allOrders = orderManagementStore.table.orders;
    const { handlePreShip } = usePreShip({
        getSelectedOrders,
        allOrders,
        onFinished(errorDataSource) {
            if (!errorDataSource.length) {
                message.success("订单已预发货");
            }

            orderManagementStore.fetchOrders(shop.id, tab);
            orderManagementStore.getOrderListCount(shop.id);
        },
    });

    const onRefreshOrderAndListCount = () => {
        onRefresh();
        orderManagementStore.getOrderListCount();
    };

    const { submitReship } = useReship(onRefreshOrderAndListCount);

    const { downloading: customizationDownloading, handleDownload: handleDownloadCustomization } =
        useDownloadCustomization(getSelectedOrders);

    const cancelDropshippingOrders = useCancelDropshippingOrdersClick(onRefreshOrderAndListCount);

    const { runAsync: handleBatchCancelMergeOrder, loading: batchCancelMergeOrderLoading } =
        useBatchCancelMergeOrderDialog(onRefresh);

    const { handleSyncOrders, loading: batchSyncOrderLoading } = useSupplierBatchSyncOrders();

    const { runAsync: handlePddEwaybillCancelRun, loading: pddFdsEwaybillCancelLoading } = useRequest(pddFdsEwaybillCancel, {
        manual: true,
    });

    const { handleCopyOrderInfoClick } = useCopyOrderInfoClick(shop?.id, tab);

    const history = useHistory();

    const { runAsync: handleMenuClick, loading: menuClickLoading } = useRequest(
        async (info: MenuInfo) => {
            onMenuClick?.(info);

            const { key } = info;

            // 导出订单信息 可以不选中订单
            if (key === OperationKey.EXPORT_ORDER) {
                const selectedOrders = orderManagementStore.table.selectedOrders;
                const prevQueryParams = orderManagementStore.table.prevQueryParams;

                modalStore.openModal(ExportOrderInfoModal, {
                    shopId: shop.id,
                    orders: selectedOrders,
                    queryParams: prevQueryParams,
                });

                return;
            } else if (key === OperationKey.BATCH_UNLOCK) {
                const lockedOrders = orderManagementStore.table.orders?.filter(
                    (order) => !!orderManagementStore.table.orderLockStore.lockOrderIdMapping.get(order.id),
                );

                const manualLockOrders = orderManagementStore.table.orders?.filter(
                    (order) => !!orderManagementStore.table.orderLockStore.manualLockOrderIdMapping.get(order.id),
                );

                if (manualLockOrders.length) {
                    await unlockOrder(shop.id, manualLockOrders);

                    manualLockOrders.forEach((order) => {
                        const newTags = [...(order.tags || [])];
                        remove(newTags, (tag) => tag === OrderTag.LOCKED);
                        orderManagementStore.table.updateOrder(order.id, { tags: newTags });
                    });
                }

                lockedOrders.forEach((order) => {
                    orderManagementStore.table.orderLockStore.handleUnlockOrder(order.id);
                });

                message.success("批量解锁成功");

                collectEvent(DataTrackEvent.UNLOCK_ORDER, {
                    shop_id: shop.id,
                    shop_name: shop.name,
                    shop_type: ShopTypeNameMap[shop.type],
                    order_count: lockedOrders.length,
                    context: OrderTabNameMap[tab],
                });

                return;
            } else if (key === OperationKey.EXPORT_PICK_LIST) {
                const selectedOrders = orderManagementStore.table.selectedOrders;
                const prevQueryParams = orderManagementStore.table.prevQueryParams;

                modalStore.openModal(ExportOrderPickListModal, {
                    shopId: shop.id,
                    orders: selectedOrders,
                    prevQueryParams,
                });

                return;
            } else if (key === OperationKey.EXPORT_EXPRESS) {
                const selectedOrders = orderManagementStore.table.selectedOrders;
                const prevQueryParams = orderManagementStore.table.prevQueryParams;

                modalStore.openModal(ExportOrderExpressModal, {
                    shopId: shop.id,
                    orders: selectedOrders,
                    prevQueryParams,
                });

                return;
            } else if (key === OperationKey.BATCH_MERGE_ORDER) {
                const selectedOrders = orderManagementStore.table.selectedOrders;
                const prevQueryParams = orderManagementStore.table.prevQueryParams;

                modalStore.openModal(ConfirmMergeOrderModal, {
                    shopId: shop.id,
                    orders: selectedOrders,
                    prevQueryParams,
                    onDone() {
                        onRefresh();
                    },
                });

                return;
            } else if (key === OperationKey.EXPORT_PRINT_PURCHASING_LABEL) {
                const selectedOrders = orderManagementStore.table.selectedOrders;
                const prevQueryParams = orderManagementStore.table.prevQueryParams;

                modalStore.openModal(ExportOrderPurchasingLabelModal, {
                    shopId: shop.id,
                    orders: selectedOrders,
                    prevQueryParams,
                    onPrintDone: onRefresh,
                });

                return;
            } else if (key === OperationKey.AUTO_PRINT) {
                history.push("/features/autoPrint");
                return;
            } else if (key === OperationKey.INSTANT_SHIP) {
                const currentTask = await orderManagementStore.instantShipStore.loadCurrentTask(shop.id, false);

                if (currentTask) {
                    modalStore.openModal(InstantShipTaskModal, {
                        shop,
                        instantShipStore: orderManagementStore.instantShipStore,
                        taskId: currentTask.id,
                    });
                } else {
                    modalStore.openModal(InstantShipStartModal, {
                        shop,
                        instantShipStore: orderManagementStore.instantShipStore,
                    });
                }

                return;
            } else if (key === OperationKey.SCAN_PRINT) {
                history.push("/features/scanPrinting");
                return;
            }

            const selectedOrders = getSelectedOrders();

            if (selectedOrders.length === 0) {
                return;
            }

            if (key === OperationKey.DELETE_WAYBILL_NO) {
                handleDeleteWaybillNoClick();
            } else if (key === OperationKey.PRINT_PACKING_LIST) {
                await handlePrintPackingList();
            } else if (key === OperationKey.PRINT_ORDER_ITEM_LABEL) {
                handlePrintOrderItemLabel();
            } else if (key === OperationKey.EXPORT_SHIPPING_TEMPLATE) {
                modalStore.openModal(ExportShippingTemplateDialog, {
                    orders: selectedOrders,
                });
            } else if (key === OperationKey.DOWNLOAD_CUSTOMIZATION) {
                handleDownloadCustomization();
            } else if (key === OperationKey.BATCH_CANCEL_MERGE_ORDER) {
                handleBatchCancelMergeOrder(selectedOrders);
            } else if (key === OperationKey.PRE_SHIP) {
                await globalStore.premiumFeatureTrialStore.getNodePremiumFeatureTrial([shop.id]);

                function buildExtraContent() {
                    const premiumFeatureTrial = globalStore.premiumFeatureTrialStore.premiumFeatureTrialMapping.get(
                        shop.id,
                    );
                    if (isEmpty(premiumFeatureTrial?.PRE_SHIPPING)) return null;

                    const shopSetting = globalStore.shopSettingMapping.get(shop.id);
                    const appVersionName = AppVersionNameMap[shopSetting?.appVersion];

                    return (
                        <div className="error mt10">
                            {appVersionName}店铺&nbsp;:&nbsp;还能试用{premiumFeatureTrial?.PRE_SHIPPING}次
                        </div>
                    );
                }

                const extraContent = buildExtraContent();

                await handlePreShip(extraContent);
            } else if (key === OperationKey.REISSUE_PACKAGE) {
                const selectedOrders = getSelectedOrders();

                await execReissueOrder({
                    globalStore,
                    modalStore,
                    selectedOrders,
                    onShipTaskDone: () => orderManagementStore.fetchOrders(shop.id, tab),
                });
            } else if (key === OperationKey.RESHIP) {
                const selectedOrders = getSelectedOrders();

                await execReshipOrder({
                    modalStore,
                    selectedOrders,
                    globalStore,
                    allOrders,
                    updateOrder: orderManagementStore.table.updateOrder,
                    reshipOrder: async (canReshipOrders) => {
                        await submitReship({ orders: canReshipOrders });
                    },
                });
            } else if (key === OperationKey.UPDATE_SELLER_REMARKS) {
                openBatchUpdateSellerRemarksModal({
                    shopId: shop.id,
                    orders: selectedOrders,
                    modalStore,
                    globalStore,
                    onUpdateOrderSellerRemarks: orderManagementStore.table.updateSellerRemarks,
                });
            } else if (key === OperationKey.ORDER_MARK) {
                modalStore.openModal(BatchSetOrderMarkModal, {
                    orders: selectedOrders,
                    onSuccess: onRefresh,
                });
            } else if (key === OperationKey.SPLIT_ORDER) {
                handleBatchSplitOrder(selectedOrders, globalStore, modalStore, onRefresh);
            } else if (key === OperationKey.CANCEL_SPLIT_ORDER) {
                batchCancelSplitOrders(selectedOrders, modalStore, globalStore, onRefresh, shop.id);
            } else if (key === OperationKey.BATCH_SYNC_ORDERS) {
                modalStore.openModal(SyncOrderProcessDialog, {
                    shopId: shop.id,
                    orderSyncType: "BATCH_RESYNC",
                    orders: selectedOrders,
                });

                try {
                    collectEvent(DataTrackEvent.RESYNC_ORDER, {
                        shop_id: shop.id,
                        shop_name: shop.name,
                        shop_type: ShopTypeNameMap[shop.type],
                        order_count: selectedOrders.length,
                    });
                } catch {}
            } else if (key === OperationKey.HOLD_ORDER) {
                // 取出是代发订单，并且还没有退款的
                const dropShippingOrders = selectedOrders.filter((order) => {
                    return order.retailerDropShippingInfo && !order.retailerDropShippingInfo.refundStatus;
                });

                // 如果有代发订单
                if (dropShippingOrders.length) {
                    const goNext = await new Promise<boolean>((yes) => {
                        Modal.confirm({
                            width: 466,
                            title: "选择的订单中有代发订单，如挂起不发货，可以先退款代发订单再操作或联系供货商说明，以免供货商发货",
                            content: (
                                <div className="mt20">
                                    <a
                                        className="mr5"
                                        href="#"
                                        onClick={(e) => {
                                            e.preventDefault();

                                            const shouldNext = globalStore.checkUserAuthority(
                                                Authority.ROLE_DROP_SHIPPING_REFUND_MANAGEMENT,
                                            );

                                            if (!shouldNext) {
                                                return;
                                            }

                                            modalStore.openModal(BatchRefundDropShippingOrderModal, {
                                                orders: dropShippingOrders,
                                                onComplete: () => {
                                                    dropShippingOrders.map((order) => {
                                                        order.retailerDropShippingInfo = null;
                                                    });
                                                },
                                            });
                                        }}
                                    >
                                        退款选择的代发订单
                                    </a>
                                    <span className="text-gray text-small">点击可直接退款代发订单，在操作挂起</span>
                                </div>
                            ),
                            okText: "取消",
                            onOk() {
                                yes(false);
                            },
                            cancelText: "继续挂起",
                            onCancel() {
                                yes(true);
                            },
                        });
                    });

                    if (!goNext) {
                        return;
                    }
                }

                modalStore.openModal(HoldOrderModal, {
                    orders: selectedOrders,
                    onSuccess: onRefreshOrderAndListCount,
                });
            } else if (key === OperationKey.BATCH_CANCEL_EWAYBILL_NOS) {
                modalStore.openModal(BatchCancelEwaybillNoModal, {
                    orders: selectedOrders,
                    shopId: shop.id,
                });
            } else if (key === OperationKey.BATCH_DELETE_EWAYBILL_NOS) {
                modalStore.openModal(BatchDeleteEWaybillNoModal, {
                    orders: selectedOrders,
                });
            } else if (key === OperationKey.BATCH_REPRINT_EWAYBILLS) {
                modalStore.openModal(BatchReprintEwaybillModal, {
                    orders: selectedOrders,
                    isFromReprint: true,
                });
            } else if (key === OperationKey.UPDATE_LOGISTICS_COMPANY) {
                modalStore.openModal(UpdateLogisticsCompanyDialog, {
                    orders: selectedOrders,
                });
            } else if (key === OperationKey.UPDATE_PRINTING_STATUS) {
                modalStore.openModal(UpdatePrintingStatusDialog, {
                    orders: selectedOrders,
                });
            } else if (key === OperationKey.BATCH_SUBMIT_ORDER) {
                let { filterOrders, errorNodes } = getValidOrders(globalStore, selectedOrders);

                if (!filterOrders.length) {
                    return Modal.error({ title: "提交订单失败", content: errorNodes });
                }

                filterOrders = await preValidateAndFilterOrdersOrExecOperation(
                    globalStore,
                    modalStore,
                    filterOrders,
                    companySettingStore.ensureHintBlacklistDetail,
                    onRefresh,
                );

                modalStore.openModal(DropShippingBatchPushOrderModal, {
                    shopId: shop?.id,
                    orders: filterOrders,
                    onSuccess: (results: PushOrderResult[]) => {
                        for (const { successful, retailerOrderId, retailerDropShippingInfo } of results) {
                            if (successful) {
                                const newOrder = filterOrders.find(({ id }) => id === retailerOrderId);

                                assign(newOrder, { retailerDropShippingInfo });

                                orderManagementStore.table.updateOrder(retailerOrderId, {
                                    retailerDropShippingInfo: retailerDropShippingInfo,
                                });
                            }
                        }
                    },
                });
            } else if (key === OperationKey.BATCH_REFUND) {
                modalStore.openModal(BatchRefundDropShippingOrderModal, {
                    orders: selectedOrders,
                    onComplete: onRefresh,
                });
            } else if (key === OperationKey.SUPPLIER_BATCH_SYNC_ORDERS) {
                handleSyncOrders(selectedOrders);

                collectEvent(DataTrackEvent.RESYNC_ORDER, {
                    shop_id: shop.id,
                    shop_name: shop.name,
                    shop_type: ShopTypeNameMap[shop.type],
                    order_count: selectedOrders.length,
                });
            } else if (key === OperationKey.PDD_FDS_EWAYBILL_CANCEL) {
                await handlePddEwaybillCancelRun(selectedOrders);
                message.success("取消回传运单号成功");
            } else if (key === OperationKey.SUPPLIER_BATCH_MODIFY_DROPSHIPPING_REMARK) {
                openBatchUpdateSellerRemarksModal({
                    orders: selectedOrders,
                    onUpdateSupplierOrderRemarks: orderManagementStore.table.updateSupplierOrderRemarks,
                    modalStore,
                    globalStore,
                    shopId: shop.id,
                });
            }
            // 发货易供货商取消代发订单
            else if (key === OperationKey.CANCEL_DROPSHIPPING_ORDER) {
                await cancelDropshippingOrders(selectedOrders);
            } else if (key === OperationKey.BATCH_UPDATE_IMEI) {
                modalStore.openModal(BatchUpdateIMEIModal, {
                    orders: selectedOrders,
                    onRefresh,
                    shopId: shop.id,
                });
            } else if (key === OperationKey.QUALITY_INSPECTION_CODE) {
                const pass = selectedOrders.every((item) =>
                    item.tags?.includes(OrderTag.CHANNELSHOP_QUALITY_INSPECTION),
                );

                if (!pass) {
                    Modal.error({ title: "您勾选的订单有非送检订单，不支持打印质检码。请只选择送检订单再来操作打印" });
                    return;
                }

                modalStore.openModal(PrintQuantityInspectionCodeModal, { orders: selectedOrders });
            } else if (key === OperationKey.BATCH_UPDATE_SHIPPER) {
                modalStore.openModal(UpdateShippingAddressModal, {
                    shopId: shop.id,
                    tab,
                    orders: selectedOrders,
                });
            } else if (key === OperationKey.BATCH_LOCK) {
                const hasDropshippingRefunding = selectedOrders.some(
                    (order) => order.retailerDropShippingInfo && !order.retailerDropShippingInfo.refundStatus,
                );

                if (hasDropshippingRefunding) {
                    await confirm(
                        "选择的订单中有代发订单，如挂起不发货，可以先退回代发订单再操作或联系供货商说明，以免供货商发货",
                    );
                }

                await lockOrder(shop.id, selectedOrders);

                message.success("已锁定");

                selectedOrders.forEach((order) => {
                    const newTags = order?.tags || [];

                    newTags.push(OrderTag.LOCKED);

                    orderManagementStore.table.updateOrder(order.id, { tags: newTags });

                    orderManagementStore.table.unselectOrder(order.id);

                    orderManagementStore.table.orderLockStore.manualLockOrderIdMapping.set(order.id, true);
                });

                collectEvent(DataTrackEvent.LOCK_ORDER, {
                    shop_id: shop.id,
                    shop_name: shop.name,
                    shop_type: ShopTypeNameMap[shop.type],
                    order_count: selectedOrders.length,
                    context: OrderTabNameMap[tab],
                });
            } else if (key === OperationKey.COPY_ORDER_INFO) {
                await handleCopyOrderInfoClick(selectedOrders);
            } else if (key === OperationKey.PUSH_ORDER_TO_FACTORY) {
                if (import.meta.env.VITE_IS_QN) {
                    Modal.error({
                        title: "提示",
                        content: "当前操作只能在网页版发货易操作",
                    });

                    return;
                }

                const splitOrder = selectedOrders.find(
                    (it) => it.mergeSplitStatus === OrderMergeSplitStatus.SPLIT_CHILD,
                );

                if (splitOrder) {
                    Modal.error({
                        title: "提示",
                        content: "暂时不能推送拆分子订单",
                    });

                    return;
                }

                // 推送订单到工厂
                const hasLogin = await hasLoginLaihuoV2();

                if (!hasLogin) {
                    await confirm("提示", "您尚未登录来火，是否跳转登陆？");

                    window.open(`${dzAdminHost}/login`);

                    return;
                }

                await pushOrderToFactory(cloneDeep(toJS(selectedOrders)));
            }
        },
        {
            manual: true,
        },
    );

    const loading =
        deleteWaybillNoLoading ||
        customizationDownloading ||
        batchSyncOrderLoading ||
        pddFdsEwaybillCancelLoading ||
        batchCancelMergeOrderLoading ||
        menuClickLoading;

    useEffect(() => {
        onLoadingChange?.(loading);
    }, [onLoadingChange, loading]);

    const showPackListExport =
        tab === OrderTab.PENDING_SHIPMENT || tab === OrderTab.ON_HOLD || tab === OrderTab.SHIPPED;

    /** 供货商的手工店铺不允许提交订单 */
    const isSupplierManualShop = getIsSupplierManualShop(shop, globalStore);

    const showBatchSubmitOrder =
        ![OrderTab.SHIPPED_REFUNDING, OrderTab.PENDING_SHIPMENT_REFUNDING].includes(tab) &&
        shouldShowDropShippingDetail(shop) &&
        !isSupplierManualShop;

    const showBatchPayOrder = false;

    const runOperationByKey = async (key: OperationKey) => {
        await handleMenuClick({ key } as MenuInfo);
    };

    return {
        handleMenuClick,
        runOperationByKey,
        loading,
        isRetailer: globalStore.companyRoleStore.isRetailer,
        fulfillmentType,
        showPackListExport,
        showBatchSubmitOrder,
        showBatchPayOrder,
        companyId: globalStore.companyId,
    };
};
